"use client";
import {
  animate,
  MotionValue,
  useDragControls,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
} from "framer-motion";
import { useLayoutEffect, useRef, useState } from "react";
import { clamp } from "@/utils/clamp";
import {
  DEFAULT_PANEL_METRICS,
  getPanelMetrics,
  PanelMetrics,
} from "@/app/(pages)/Home/constants/mobile";

let cachedSafeTop: number | null = null;
const readSafeAreaTop = (): number => {
  if (cachedSafeTop !== null) return cachedSafeTop;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;top:env(safe-area-inset-top,0px);height:0;visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  cachedSafeTop = probe.getBoundingClientRect().top;
  probe.remove();
  return cachedSafeTop;
};

const RESIZE_DEBOUNCE_MS = 150;

const PANEL_VISUALS = {
  open: {
    mainWeatherDisplay: "none",
    panelHeaderDisplay: "flex",
    overflowY: "scroll",
    height: "48px",
    y: "16px",
    boxShadow: "inset 0px 5px 5px -4px #FFFFFF33",
  },
  closed: {
    mainWeatherDisplay: "inline-flex",
    panelHeaderDisplay: "none",
    overflowY: "hidden",
    height: "24px",
    y: "64px",
    boxShadow: "inset 0px 5px 5px -4px #ffffff",
  },
} as const;

const animationInitialization = ({
  panelY,
  initialY,
  pullUp,
  isInitialized,
  isHorizontal,
}: {
  panelY: MotionValue<number>;
  initialY: number;
  pullUp: number;
  isInitialized: boolean;
  isHorizontal: boolean;
}) => {
  const stationaryContentY = useTransform(panelY, (latestY) =>
    isInitialized ? -latestY : 0,
  );

  const backgroundOverlayOpacity = useTransform(
    panelY,
    [pullUp, initialY],
    isInitialized ? [1, 0] : [0, 0],
  );

  const mainWeatherOpacity = useTransform(
    panelY,
    [initialY, initialY * 0.65],
    isInitialized && !isHorizontal ? [1, 0] : [1, 1],
  );

  const panelHeaderOpacity = useTransform(mainWeatherOpacity, [1, 0], [0, 1]);

  const followerY = useSpring(panelY, {
    stiffness: 260,
    damping: 22,
    mass: 0.8,
  });

  const rawLag = useTransform(
    [followerY, panelY],
    ([f, p]: [number, number]) => f - p,
  );

  const panelLagY = useTransform(rawLag, (v) =>
    isInitialized ? clamp(v, -10, 10) : 0,
  );

  const mainWeatherDisplay = useMotionValue<string>(
    PANEL_VISUALS.closed.mainWeatherDisplay,
  );
  const panelHeaderDisplay = useMotionValue<string>(
    PANEL_VISUALS.closed.panelHeaderDisplay,
  );
  const overflowY = useMotionValue<string>(PANEL_VISUALS.closed.overflowY);
  const borderRadius = useMotionValue<string>(isHorizontal ? "0px" : "32px");
  const height = useMotionValue<string>(PANEL_VISUALS.closed.height);
  const y = useMotionValue<string>(PANEL_VISUALS.closed.y);
  const panelBoxShadow = useMotionValue<string>(PANEL_VISUALS.closed.boxShadow);

  return {
    stationaryContentY,
    backgroundOverlayOpacity,
    mainWeatherOpacity,
    mainWeatherDisplay,
    panelHeaderOpacity,
    panelHeaderDisplay,
    panelLagY,
    overflowY,
    borderRadius,
    height,
    y,
    panelBoxShadow,
  };
};

export const useWeatherPanelAnimation = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialY, setInitialY] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<PanelMetrics>(DEFAULT_PANEL_METRICS);
  const [panelOpen, setPanelOpen] = useState(false);
  const controls = useDragControls();
  const dragStartRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelY = useMotionValue(0);
  const openRef = useRef<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { isHorizontal, pullUp } = metrics;

  const animationValues = animationInitialization({
    panelY,
    initialY,
    pullUp,
    isInitialized,
    isHorizontal,
  });

  const commitPanelVisuals = (open: boolean, horizontal: boolean) => {
    const v = open ? PANEL_VISUALS.open : PANEL_VISUALS.closed;
    animationValues.mainWeatherDisplay.set(v.mainWeatherDisplay);
    animationValues.panelHeaderDisplay.set(v.panelHeaderDisplay);
    animationValues.overflowY.set(v.overflowY);
    animationValues.height.set(v.height);
    animationValues.y.set(v.y);
    animationValues.panelBoxShadow.set(v.boxShadow);
    animationValues.borderRadius.set(open || horizontal ? "0px" : "32px");
  };

  useLayoutEffect(() => {
    let resizeTimer: number | null = null;
    const prev: { m: PanelMetrics | null; initialY: number | null } = {
      m: null,
      initialY: null,
    };

    const check = (isFirst: boolean) => {
      const vv = window.visualViewport;
      const innerHeight = vv?.height ?? window.innerHeight;
      const innerWidth = vv?.width ?? window.innerWidth;

      const m = getPanelMetrics(innerHeight, innerWidth, readSafeAreaTop());
      const calcInitialY =
        innerHeight - (m.isHorizontal ? m.visibleShort : m.visible);

      const p = prev.m;
      if (
        p &&
        p.isHorizontal === m.isHorizontal &&
        p.pullUp === m.pullUp &&
        p.visible === m.visible &&
        p.visibleShort === m.visibleShort &&
        prev.initialY === calcInitialY
      ) {
        return;
      }
      const horizontalChanged = !p || p.isHorizontal !== m.isHorizontal;
      prev.m = m;
      prev.initialY = calcInitialY;

      setMetrics(m);

      const root = document.documentElement;
      root.style.setProperty("--pull-up", `${m.pullUp}px`);
      root.style.setProperty("--panel-visible", `${m.visible}px`);

      if (!m.isHorizontal && horizontalChanged && scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }

      setInitialY(calcInitialY);

      const nextOpen = !m.isHorizontal && !isFirst && openRef.current;
      openRef.current = nextOpen;
      setPanelOpen(nextOpen);
      panelY.set(
        m.isHorizontal ? m.pullUp : nextOpen ? m.pullUp : calcInitialY,
      );
      commitPanelVisuals(nextOpen, m.isHorizontal);
    };

    check(true);

    const onResize = () => {
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        check(false);
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.visualViewport?.addEventListener("resize", onResize, {
      passive: true,
    });

    const idle = (cb: () => void) =>
      (window.requestIdleCallback || ((fn: any) => setTimeout(fn, 0)))(cb);
    const idleId = idle(() => setIsInitialized(true));

    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      (window.cancelIdleCallback || clearTimeout)(idleId as number);
    };
  }, []);

  useMotionValueEvent(panelY, "change", (y) => {
    if (initialY == null) return;
    const midpoint = pullUp + (initialY - pullUp) / 2;
    const nextOpen = y < midpoint;
    if (nextOpen !== openRef.current) {
      openRef.current = nextOpen;
      setPanelOpen(nextOpen);
    }
  });

  const handleDragEnd = () => {
    if (initialY == null) return;
    const threshold =
      (dragStartRef.current ?? initialY) < pullUp + 10
        ? initialY * 0.15
        : initialY * 0.85;

    const willOpen = panelY.get() < threshold;
    commitPanelVisuals(willOpen, isHorizontal);
    animate(panelY, willOpen ? pullUp : initialY, {
      type: "spring",
      bounce: 0.2,
      duration: 0.4,
    });
  };

  const togglePanel = () => {
    if (initialY == null) return;
    const willOpen = !panelOpen;
    commitPanelVisuals(willOpen, isHorizontal);
    animate(panelY, willOpen ? pullUp : initialY, {
      type: "spring",
      bounce: 0.2,
      duration: 0.4,
    });
  };

  const startDrag = (e) => {
    if (isHorizontal) return;
    controls.start(e);
  };

  return {
    isInitialized,
    isHorizontal,
    isPanelReady: initialY !== null,
    panelOpen,
    togglePanel,
    initialY,
    pullUp,
    panelY,
    controls,
    panelRef,
    dragStartRef,
    handleDragEnd,
    startDrag,
    scrollRef,
    ...animationValues,
  };
};
